import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { restRequest } from './restRequest';
import { ContentData } from '@/types';
import * as Sentry from '@sentry/nextjs';
import { BuilderError } from '@/types/errors';

type DynamicPhoneNumberData = {
	phoneNumber: string;
	everflowAffiliateId: number;
	everflowOfferId: number;
};

export type DynamicPhoneNumber = {
	phoneNumber: string;
	everflowAffiliateId: number;
	everflowOfferId: number;
	createdDate: number;
};

export const useDynamicPhoneNumber = ({
	showDynamicPhoneNumber,
}: {
	showDynamicPhoneNumber: boolean;
}) => {
	const router = useRouter();
	const [dynamicPhoneNumber, setDynamicPhoneNumber] = useState<DynamicPhoneNumber | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchDynamicPhoneNumber = async () => {
			if (!showDynamicPhoneNumber) {
				setLoading(false);
				setDynamicPhoneNumber(null);
				return;
			}

			if (!router.isReady) {
				return;
			}

			try {
				setLoading(true);

				// Extract ef_affiliate_id and ef_offer_id from router query
				const efAffiliateId = Number(router.query.ef_affiliate_id);
				const efOfferId = Number(router.query.ef_offer_id);

				// If no parameters are present, return null
				if (!efAffiliateId || !efOfferId) {
					setDynamicPhoneNumber(null);
					return;
				}

				const allData = await restRequest<ContentData<DynamicPhoneNumberData, 'data'>[]>({
					modelName: 'dynamic-phone-number',
					sortBy: 'createdDate',
				});

				// Filter client-side for matching affiliate and offer IDs
				const data =
					allData?.filter(
						(entry) =>
							entry.data.everflowAffiliateId === efAffiliateId &&
							entry.data.everflowOfferId === efOfferId,
					) || [];

				if (data?.length) {
					// Filter out archived entries
					const publishedData = data.filter((entry) => entry.published === 'published');

					if (publishedData.length > 0) {
						// Data is already sorted by createdDate descending from the API
						// Just take the first non-archived entry (which will be the latest)
						const latestEntry = publishedData[0];

						const phoneNumberData: DynamicPhoneNumber = {
							phoneNumber: latestEntry.data.phoneNumber,
							everflowAffiliateId: latestEntry.data.everflowAffiliateId,
							everflowOfferId: latestEntry.data.everflowOfferId,
							createdDate: latestEntry.createdDate,
						};

						setDynamicPhoneNumber(phoneNumberData);
					} else {
						// All entries are archived
						setDynamicPhoneNumber(null);

						Sentry.withScope((scope) => {
							scope.setTag('api', 'Builder');
							scope.setLevel('info');
							scope.setContext('dynamic_phone_search', {
								efAffiliateId,
								efOfferId,
								reason: 'all_entries_archived',
							});
							Sentry.captureMessage(
								`All dynamic phone number entries are archived for ef_affiliate_id: ${efAffiliateId}, ef_offer_id: ${efOfferId}`,
							);
						});
					}
				} else {
					// No matching dynamic phone number found
					setDynamicPhoneNumber(null);

					Sentry.withScope((scope) => {
						scope.setTag('api', 'Builder');
						scope.setLevel('info');
						scope.setContext('dynamic_phone_search', {
							efAffiliateId,
							efOfferId,
						});
						Sentry.captureMessage(
							`No dynamic phone number found for ef_affiliate_id: ${efAffiliateId}, ef_offer_id: ${efOfferId}`,
						);
					});
				}
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : String(err);
				setDynamicPhoneNumber(null);

				Sentry.withScope((scope) => {
					scope.setTag('api', 'Builder');
					scope.setLevel('error');
					scope.setContext('dynamic_phone_error', {
						efAffiliateId: router.query.ef_affiliate_id,
						efOfferId: router.query.ef_offer_id,
					});
					Sentry.captureException(
						new BuilderError(`Failed to fetch dynamic phone number: ${errorMessage}`),
					);
				});
			} finally {
				setLoading(false);
			}
		};

		fetchDynamicPhoneNumber();
	}, [router.isReady, router.query, showDynamicPhoneNumber]);

	return { dynamicPhoneNumber: dynamicPhoneNumber?.phoneNumber || null, loading };
};
